import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { useToast } from '../context/ToastContext';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { success, error } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName: name });
      success('Account created successfully! Welcome to Kumbhar Samaj.');
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        error('This email is already registered. Please login instead.');
      } else {
        error('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Left Side - Visual */}
        <div className="auth-visual">
          <div className="auth-visual-content">
            <div className="auth-brand">
              <img
                src="/logo.png"
                alt="Kumbhar Samaj Logo"
                className="brand-logo"
              />
              <span className="brand-text">Kumbhar Samaj Onnati Mandal</span>
            </div>
            <h2>Join Our Community</h2>
            <p>Create your account and start your journey to find the perfect life partner.</p>
            <div className="auth-features">
              <div className="feature-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <polyline points="17 11 19 13 23 9"/>
                </svg>
                <span>Create Detailed Profile</span>
              </div>
              <div className="feature-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <span>Connect with Matches</span>
              </div>
              <div className="feature-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <span>Safe & Secure Platform</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="auth-form-wrapper">
          <div className="auth-form">
            <div className="form-header">
              <h1>Create Account</h1>
              <p>Sign up to get started with your matrimony profile</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                  autoComplete="name"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password (min 6 characters)"
                  required
                  autoComplete="new-password"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Confirm Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  autoComplete="new-password"
                />
              </div>

              <div className="form-agreement">
                <label className="checkbox-label">
                  <input type="checkbox" required />
                  <span>
                    I agree to the <Link to="/terms">Terms of Service</Link> and{' '}
                    <Link to="/privacy">Privacy Policy</Link>
                  </span>
                </label>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary btn-lg w-full"
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            <div className="form-divider">
              <span>Already have an account?</span>
            </div>

            <Link to="/login" className="btn btn-outline btn-lg w-full">
              Sign In
            </Link>
          </div>
        </div>
      </div>

      <style jsx>{`
        .auth-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: var(--space-4);
          background: var(--neutral-50);
        }

        .auth-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          max-width: 1200px;
          width: 100%;
          background: white;
          border-radius: var(--radius-2xl);
          overflow: hidden;
          box-shadow: var(--shadow-xl);
        }

        .auth-visual {
          background: linear-gradient(135deg, var(--primary-700) 0%, var(--primary-500) 100%);
          padding: var(--space-16);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .auth-visual-content {
          max-width: 480px;
        }

        .auth-brand {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          font-family: var(--font-display);
          font-size: var(--text-2xl);
          font-weight: var(--weight-bold);
          margin-bottom: var(--space-8);
        }

        .brand-icon {
          font-size: var(--text-4xl);
        }

        .brand-logo {
          width: 50px;
          height: 50px;
          object-fit: cover;
          border-radius: 50%;
          border: 2px solid #d6af36;
          background: white;
        }

        .auth-visual h2 {
          font-size: var(--text-4xl);
          color: white;
          margin-bottom: var(--space-4);
          line-height: var(--leading-tight);
        }

        .auth-visual p {
          font-size: var(--text-lg);
          color: rgba(255, 255, 255, 0.9);
          line-height: var(--leading-relaxed);
          margin-bottom: var(--space-12);
        }

        .auth-features {
          display: flex;
          flex-direction: column;
          gap: var(--space-4);
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          font-size: var(--text-base);
          color: rgba(255, 255, 255, 0.95);
        }

        .auth-form-wrapper {
          padding: var(--space-12);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .auth-form {
          width: 100%;
          max-width: 420px;
        }

        .form-header {
          text-align: center;
          margin-bottom: var(--space-8);
        }

        .form-header h1 {
          font-size: var(--text-4xl);
          margin-bottom: var(--space-2);
        }

        .form-header p {
          color: var(--neutral-600);
          margin: 0;
        }

        .form-group {
          margin-bottom: var(--space-5);
        }

        .form-agreement {
          margin-bottom: var(--space-6);
        }

        .checkbox-label {
          display: flex;
          align-items: flex-start;
          gap: var(--space-2);
          font-size: var(--text-sm);
          color: var(--neutral-700);
          cursor: pointer;
          line-height: var(--leading-relaxed);
        }

        .checkbox-label a {
          color: var(--primary-500);
          text-decoration: none;
        }

        .checkbox-label a:hover {
          text-decoration: underline;
        }

        .w-full {
          width: 100%;
        }

        .form-divider {
          text-align: center;
          margin: var(--space-8) 0;
          position: relative;
        }

        .form-divider::before,
        .form-divider::after {
          content: '';
          position: absolute;
          top: 50%;
          width: 40%;
          height: 1px;
          background: var(--neutral-200);
        }

        .form-divider::before {
          left: 0;
        }

        .form-divider::after {
          right: 0;
        }

        .form-divider span {
          background: white;
          padding: 0 var(--space-4);
          font-size: var(--text-sm);
          color: var(--neutral-600);
        }

        @media (max-width: 768px) {
          .auth-container {
            grid-template-columns: 1fr;
          }

          .auth-visual {
            display: none;
          }

          .auth-form-wrapper {
            padding: var(--space-8);
          }
        }
      `}</style>
    </div>
  );
};

export default RegisterPage;